import React from 'react';
import { useTranslation } from 'react-i18next';
import AnchorBar from './AnchorBar/AnchorBar';
import RockSlope from './RockSlope/RockSlope';
import Mesh from './Mesh/Mesh';
import SafetyCoefficients from './SafetyCoefficients/SafetyCoefficients';
import AccordionsGroup from '../../../../components/AccordionsGroup/AccordionsGroup';
import { getMacro1ProjectAsync, putMacro1ProjectAsync } from '../../../api/store/BeMaccaferriStoreHelper';
import { setProject, dataToProject, projectToData, commitProject, setReadOnly, setHasBeenRan, setDisableRun} from '../../store/project/projectslice';
import { setOutputData,} from '../../store/project/resultsSlice';
import { useParams } from "react-router-dom";
import Dialog from '../../../../components/Dialog/Dialog';
import { useEffect } from 'react';
import { IMacro1ProjectPutData } from '../../../api/IApiRequest';
import { useAppDispatch, useAppSelector } from '../../../../reduxService/hooks';
import { selectMacro1, selectProjectInfo, setProjectInfo, setHasBeenSent } from '../../../store/projectInfo/projectInfoSlice';
import { useNavigate } from 'react-router-dom';
import "./InputForm.scss";
import SiteEnvLevel from '../../../Macro1/components/InputForm/SiteEnvLevel/SiteEnvLevel';
import { RootState } from '../../../../reduxService';
import { useSelector } from 'react-redux';
import { getMacro1MeshTypes } from '../../../api/products/BeMaccaferriProductsHelper';

const InputForm = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [noMeshes, setNoMeshes] = React.useState<boolean>(false);
    const [onlyExport, setOnlyExport] = React.useState<boolean>(false);
    const projectInfo = useAppSelector(selectProjectInfo);

    const [ErrorDialog, setErrorDialog] = React.useState<string>('');
    const closeErrorDialog = () => {
        setErrorDialog('');
    }

    const [currentDialog, setCurrentDialog] = React.useState<string>('');
    
    const closeDialog = () => {
        setCurrentDialog('');
    }

    const optEdit = () => {
        closeDialog();
        setLoading(false);
    }

    const optExport = () => {
        sendProjectData();
        setTimeout(()=>{navigate(`/results/${macro1.project.data?.projectInfo?.projectType}/${macro1.project.data?.projectInfo?.projectId}`);}, 2000);
        dispatch(setDisableRun(false));
        dispatch(setHasBeenSent(true));
        dispatch(setHasBeenRan(true));
        setOnlyExport(true);
        setLoading(false);
        closeDialog();
    }

    const [FirstLoad, setFirstLoad] = React.useState<boolean>(true);

    const backToRoot = () => {
        window.location.href = '/';
    }

    const noMeshesCases = () => {
        dispatch(setReadOnly(true));
        if(!currentProjectM1.hasBeenRan) {
            dispatch(setDisableRun(true));
        } else {
            dispatch(setDisableRun(false));
        }
        closeErrorDialog();
    };

    const dispatch = useAppDispatch();

    const macro1 = useAppSelector(selectMacro1);

    //Check if there are meshes in the Mesh Types list
    useEffect(() => {
        async function getMacro1MeshTypesAsync() { 
            if( !!projectInfo.data?.unit){
                try {
                    const response = (await getMacro1MeshTypes()).data;
                    let meshTypeList = response;
                    if(meshTypeList.length <= 0) {
                        setNoMeshes(true);
                        setErrorDialog('error');
                    }
                } catch (error) {
                    console.error('getMacro1MeshTypes');
                    setNoMeshes(true);
                    setErrorDialog('error');
                }
            }
        }
        getMacro1MeshTypesAsync();
    }, [projectInfo.data?.unit, window.localStorage.getItem("i18nextLng")]);

    useEffect(() => {
        async function getProj() {
            if (id && !macro1.project.data?.projectInfo?.projectId) {
                try {
                    const res = await getMacro1ProjectAsync(id);
                    if (res.data.projectInfos) {
                        dispatch(setProjectInfo({ projectId: id, ...res.data.projectInfos, hasBeenSent: location.pathname.split("/")[1] == 'results' }));
                        setCurrentDialog(res.data.readOnly ? 'Open' : '');
                    } 
                    dispatch(setProject(dataToProject(res.data)));
                    dispatch(commitProject(dataToProject(res.data)));
                    if (location.pathname.split("/")[1] == 'results') {
                        if(!res.data.hasBeenRan){
                            window.location.href=`/designCondition/${res.data?.projectInfos?.projectType}/${res.data?.projectId}`;
                            return;
                        }
                        const results = (await putMacro1ProjectAsync({ ...projectToData(dataToProject(res.data)), id: res.data.projectId } as IMacro1ProjectPutData)).data;
                        dispatch(setOutputData(results));
                        dispatch(setHasBeenSent(true));
                    }
                    if (!res.data.readOnly) {
                        setLoading(false);
                    }
                } catch (error) {
                    console.error("getProj", error);
                    setErrorDialog('error');
                }
            } else {
                setCurrentDialog(macro1.project.data?.readOnly ? 'Open' : '');
                if (!macro1.project.data?.readOnly) {
                    setLoading(false);
                }
            }
        }

        if (FirstLoad) {
            getProj();
            setFirstLoad(false);
        } else {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!loading) {
            dispatch(setHasBeenSent(false));
            if (location.pathname.split("/")[1] == 'results') {
                navigate(`/designCondition/${macro1.project.data?.projectInfo?.projectType}/${macro1.project.data?.projectInfo?.projectId}`);
            }
        }
    }, [
        macro1.project.data?.anchorBar,
        macro1.project.data?.mesh,
        macro1.project.data?.rockSlope,
        macro1.project.data?.safetyCoefficients,
        macro1.project.data?.siteEnvironmentalLevelId,
        macro1.project.data?.readOnly,
        macro1.project.data?.hasBeenRan
    ]);

    useEffect(() => {
        if(window.location.href.includes('/designCondition/') && macro1.project.data?.readOnly){
            setCurrentDialog('Open');
        }
    },[window.location.href]);

    const currentProjectM1 = useSelector((state: RootState) => {
        return { ...state.Macro1.project.data };
    });

    const sendProjectData = async () => {
        try {
                currentProjectM1.id = id;
                const results = (await putMacro1ProjectAsync(projectToData(currentProjectM1)));
                dispatch(commitProject(currentProjectM1));
                dispatch(setOutputData(results.data));
            } catch (ex) {
                setErrorDialog(((ex as any)?.response?.data?.PropertiesNotValid ?? '' + t('errors.projectCommit.unspecific_error')).replaceAll(", ", "\n"));
            }
        dispatch(setHasBeenSent(true));
        dispatch(setHasBeenRan(true));
    }
    

    return (
        <div className='d-flex flex-column'>
            <Dialog
                open={!!ErrorDialog && ErrorDialog.length > 0 && !noMeshes}
                title={`${t('errors.projectGet.title')}`}
                onClose={backToRoot}
                actions={[{
                    text: 'ok',
                    callback: backToRoot,
                    color: 'primary'
                }
                ]}>
                <>
                    <div>
                        {`${t('errors.projectGet.message')}`}
                    </div>
                </>
            </Dialog>
            <Dialog
                open={!!ErrorDialog && ErrorDialog.length > 0 && noMeshes}
                title={`${t('errors.noMeshTypes.title')}`}
                onClose={noMeshesCases}
                actions={[{
                    text: 'ok',
                    callback: noMeshesCases,
                    color: 'primary'
                }
                ]}>
                <div>
                    {`${t('errors.noMeshTypes.message')}`}
                </div>
            </Dialog>
            <Dialog
                open={currentDialog!='' && !ErrorDialog && ErrorDialog == '' && !noMeshes && location.pathname.split("/")[1] == 'designCondition'}
                title={`${t('errors.unavailableMesh.title')}`}
                onClose={optEdit}
                actions={[{
                    text: t('errors.unavailableMesh.opt1'),
                    callback: optExport,
                    color: 'primary'
                },
                {
                    text: t('errors.unavailableMesh.opt2'),
                    callback: optEdit,
                    color: 'primary'}
            ]}> 
                <div>
                    {`${t('errors.unavailableMesh.message')}`}
                </div>
            </Dialog>
            {loading || noMeshes || onlyExport ? <></> : <AccordionsGroup
                items={
                    [
                        {
                            title: t('input_form_accordion_rock_slope'),
                            children: <RockSlope readOnly={macro1.project.data?.readOnly}/>
                        },
                        {
                            title: t('input_form_accordion_site_env_level'),
                            children: <SiteEnvLevel readOnly={macro1.project.data?.readOnly}/>
                        },
                        {
                            title: t('input_form_accordion_anchor_bar'),
                            children: <AnchorBar readOnly={macro1.project.data?.readOnly}/>
                        },
                        {
                            title: t('input_form_accordion_mesh'),
                            children: <Mesh readOnly={macro1.project.data?.readOnly}/>
                        },
                        {
                            title: t('input_form_accordion_safety_coefficients'),
                            children: <SafetyCoefficients readOnly={macro1.project.data?.readOnly}/>
                        }
                    ]
                } />}
        </div>
    );
};

export default InputForm;
